// RouteSimulator.tsx
// ---------------------------------------------------------------
// Marqueur animé qui parcourt un tableau de points [lat, lng].
// - Si aucun icon n’est fourni, on affiche un "pulse marker" CSS.
// - Zéro dépendance externe : tout le CSS est injecté dynamiquement.
// ---------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Marker, useMap } from "react-leaflet";
import L, { Icon, DivIcon, LatLngExpression } from "leaflet";

/* ------------------------------------------------------------------
   Props
   ------------------------------------------------------------------ */
export interface RouteSimulatorProps {
  /** Tableau de points [lat, lng] (ordre chronologique) */
  path: [number, number][];
  /** Vitesse « virtuelle » en km/h (défaut : 50) */
  speed?: number;
  /** Icône personnalisée (Icon ou DivIcon) */
  icon?: Icon | DivIcon;
  /** Recadrage initial sur tout le tracé (défaut : true) */
  autofit?: boolean;
  right?: boolean;
}

/* ------------------------------------------------------------------
   Hook : génère un DivIcon “pulse” et injecte le CSS au premier appel
   ------------------------------------------------------------------ */
function usePulseIcon(color = "#f32121"): DivIcon {
  return useMemo(() => {
    const STYLE_ID = "leaflet-pulse-css";
    if (!document.getElementById(STYLE_ID)) {
      const css = `
        .pulse{width:14px;height:14px;border-radius:50%;background:${color};position:relative}
        .pulse::after{content:'';position:absolute;inset:0;border-radius:50%;border:2px solid ${color};
          animation:pulse 1.4s ease-out infinite;transform-origin:center}
        @keyframes pulse{0%{transform:scale(1);opacity:.8}100%{transform:scale(3);opacity:0}}
      `;
      const style = Object.assign(document.createElement("style"), {
        id: STYLE_ID,
        textContent: css,
      });
      document.head.appendChild(style);
    }

    return L.divIcon({
      className: "",                                // désactive la classe par défaut
      html: '<div class="pulse"></div>',            // point + onde
      iconSize: [14, 14],
      iconAnchor: [7, 7],                           // centre géométrique
    });
  }, [color]);
}

/* ------------------------------------------------------------------
   Composant principal
   ------------------------------------------------------------------ */
const RouteSimulator: React.FC<RouteSimulatorProps> = ({
  path,
  speed = 25,
  icon,
  autofit = true,
  right
}) => {
  const map = useMap();
  const [idx, setIdx] = useState(0);               // index du point courant
  const playing = useRef(false);
  const timer = useRef<NodeJS.Timeout>();

  // Icône effective : custom ou pulse par défaut
  const defaultIcon = usePulseIcon();
  const effectiveIcon = icon ?? defaultIcon;

  /* -------- Helpers ------------------------------------------------ */
  const stop = () => {
    playing.current = false;
    if (timer.current) clearInterval(timer.current);
  };

  const reset = () => {
    stop();
    setIdx(0);
    if (autofit && path.length) {
      map.fitBounds(L.polyline(path).getBounds());
    }
  };

  const play = () => {
    if (playing.current || path.length < 2) return;
    playing.current = true;

    const mps = (speed * 1000) / 3600;             // mètres par seconde
    const dt = 1000 / mps;                         // avancer ~1 m par tick

    timer.current = setInterval(() => {
      setIdx((i) => {
        const next = i + 1;
        if (next >= path.length) {
          stop();
          return i;
        }
        return next;
      });
    }, dt);
  };

  /* ------------------------------------------------------------------
   Molette :  Shift + roulette = scrub   |   roulette seule = zoom
   ------------------------------------------------------------------ */
  useEffect(() => {
    const container = map.getContainer();

    // 1)  Écouteur WHEEL – déclenche le scrub et stoppe la propagation
    const onWheel = (e: WheelEvent) => {
      if (!e.shiftKey) return;            // ⇧ non pressé → on laisse zoomer
      e.preventDefault();                 // bloque le scroll/page
      e.stopPropagation();                // ← bloque le zoom Leaflet

      stop();                             // pause l’animation auto

      setIdx((i) => {
        const step = e.deltaY < 0 ? 1 : -1;
        return Math.min(Math.max(i + step, 0), path.length - 1);
      });
    };

    container.addEventListener("wheel", onWheel, { passive: false });

    // 2)  Keydown/keyup – (dés)active scrollWheelZoom en temps réel
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") map.scrollWheelZoom.disable();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") map.scrollWheelZoom.enable();
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    // — Nettoyage —
    return () => {
      container.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      map.scrollWheelZoom.enable();       // réassure le zoom normal
    };
  }, [map, path.length]);                 // ↻ si la carte ou le tracé change


  /* -------- Effet : reset lorsque le tracé change ------------------ */
  useEffect(() => {
    reset();
    return stop;                                   // cleanup à l’unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(path)]);

  /* -------- Rendu --------------------------------------------------- */
  if (!path.length) return null;                   // rien à afficher

  const currentPos: LatLngExpression = path[idx];
  const toolbarStyle: React.CSSProperties = {
    position: "absolute",
    top: 10,
    left: right ? undefined : 20,
    right: right ? 55 : undefined,
    zIndex: 1000,
    background: "#fff",
    padding: "4px 8px",
    borderRadius: 4,
    boxShadow: "0 1px 4px rgba(0,0,0,.3)",
    display: "flex",
    gap: 6,
    alignItems: "center",

  };



  return (
    <>
      <Marker position={currentPos} icon={effectiveIcon} />

      <div
        style={{
          ...toolbarStyle,
          opacity: 0.9,
          background: '#fff', // facultatif si tu veux voir l'opacité
          padding: '10px',
          borderRadius: '8px',
        }}
      >
        <i
          onClick={play}
          className="las la-play green"
          style={{
            fontSize: '20px',
            cursor: 'pointer',
            marginRight: '5px',
            transition: 'opacity 0.2s',
          }}
          onMouseDown={(e) => (e.currentTarget.style.opacity = '0.5')}
          onMouseUp={(e) => (e.currentTarget.style.opacity = '1')}
        ></i>

        <i
          onClick={stop}
          className="las la-stop blue"
          style={{
            fontSize: '20px',
            cursor: 'pointer',
            marginRight: '5px',
            transition: 'opacity 0.2s',
          }}
          onMouseDown={(e) => (e.currentTarget.style.opacity = '0.5')}
          onMouseUp={(e) => (e.currentTarget.style.opacity = '1')}
        ></i>

        <i
          onClick={reset}
          className="las la-redo-alt red"
          style={{
            fontSize: '19px',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
          }}
          onMouseDown={(e) => (e.currentTarget.style.opacity = '0.5')}
          onMouseUp={(e) => (e.currentTarget.style.opacity = '1')}
        ></i>
      </div>

    </>
  );
};

export default RouteSimulator;
